import * as React from 'react';
import {
    Button,
    Card,
    CardContent,
    Checkbox,
    createStyles,
    Divider,
    FormControl,
    FormControlLabel,
    FormGroup,
    Grid,
    Paper,
    Step,
    StepLabel,
    Stepper,
    Theme,
    Typography,
    withStyles
} from '@material-ui/core';
import { WithStyles } from '@material-ui/styles';
import { Folder } from '../../shared/domain';
import { Marker } from '../../models';
import { NodesTreeView, RenderTree } from '../../shared/components/node-tree';
import { PickedType } from '../../features/marker-export/marker-export';
import AccountProvider from '../../providers/account.provider';
import FolderProvider from '../../providers/folder.provider';
import _ from 'lodash';
import { SimpleListPagination } from '../../shared/components/simple-list-pagination';
import { SimpleListItem } from '../../shared/components/simple-list';
import FloorplanProvider from '../../providers/floorplan.provider';
import { CCSpinner } from '../../shared/components/cc-spinner';
import { Alert, AlertTitle } from '@material-ui/lab';
import JobProvider, { JobRequest, JobTypes, RunningTasks } from '../../providers/job.provider';
import { RootStore } from '../../stores/root.store';
import { SimpleDialog } from '../../shared/components/simple-dialog';
import { AccountFull } from '../../models/account';

export type MarkerSourceType = 'Floorplan' | 'Folder' | 'Intermediate Folder' | 'None';

enum WizardSteps {
    MarkerOrigin = 0,
    FieldSelection,
    Confirmation,
    Result
}

export interface MarkerSourceItem {
    id: string,
    name: string,
    type: MarkerSourceType
}

interface fieldInput {
    label: string;
    field: string;
    value: boolean;
}

const VALID_EXPORT_FIELDS: fieldInput[] = [
    {
        label: 'Marker ID',
        field: 'id',
        value: true
    },
    {
        label: 'Marker Name',
        field: 'name',
        value: true
    },
    {
        label: 'Floorplan Name',
        field: 'floorplan_name',
        value: true
    },
    {
        label: 'Building Name',
        field: 'folder_name',
        value: true
    },
    {
        label: 'Room Type',
        field: 'room_type',
        value: true
    },
    {
        label: 'Square Footage',
        field: 'square_footage',
        value: true
    },
    {
        label: 'Marker Prefix',
        field: 'prefix',
        value: false
    },
    {
        label: 'CrowdManager Modules',
        field: 'module_groups',
        value: false
    },
    {
        label: 'CrowdComfort Modules',
        field: 'user_module_group',
        value: false
    },
    {
        label: 'Folder ID',
        field: 'folder_id',
        value: false
    },
    {
        label: 'Floorplan ID',
        field: 'floorplan_id',
        value: false
    },
    {
        label: 'GPS Coordinates',
        field: 'lat_long',
        value: false
    },
    {
        label: 'Creation Timestamp',
        field: 'timestamp',
        value: false
    },
    {
        label: 'Last Updated Timestamp',
        field: 'last_modified',
        value: false
    },
    {
        label: 'Tableau Coordinates',
        field: 'xfdf_coords',
        value: false
    }

];

const WIZARD_STEPS = [
    {
        id: WizardSteps.MarkerOrigin,
        label: 'Marker origins'
    },
    {
        id: WizardSteps.FieldSelection,
        label: 'Select fields'
    },
    {
        id: WizardSteps.Confirmation,
        label: 'Confirm changes'
    },
    {
        id: WizardSteps.Result,
        label: 'Results'
    }
];

const styles = (theme: Theme) =>
    createStyles({
        root: {
            display: 'flex',
            flexWrap: 'wrap'
        },
        container: {
            width: '100%',
            display: 'flex',
            flexDirection: 'column'
        },
        treePaper: {
            height: '90%',
            maxHeight: '35em',
            overflowY: 'scroll',
            [theme.breakpoints.down('lg')]: {
                maxHeight: '32em'
            }
        },
        treeContainer: {
            background: '#fff',
            padding: '1em',
            height: '100%'
        },
        heading: {
            fontWeight: 700,
            marginBottom: '0.5em',
            marginTop: '0.5em'
        },
        simpleHeading: {
            marginBottom: '0.5em',
            marginTop: '0.5em'
        },
        ellipsis: {
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            overflow: 'hidden'
        },
        simpleList: {
            overflow: 'auto',
            height: '24em',
            width: '100%'
        },
        stepsContentHolder: {
            padding: '1em',
            height: '40em'
        },
        stepsHolder: {
            paddingBottom: 0
        },
        gridHolders: {
            height: '95%'
        },
        nextButton: {
            width: '8em',
            position: 'absolute',
            right: '1em',
            bottom: '1em'
        },
        centerButton: {
            width: '20em',
            display: 'block',
            marginLeft: 'auto',
            marginRight: 'auto',
            marginTop: '4em'
        },
        progressContainer: {
            flexGrow: 1
        },
        markerCard: {
            position: 'relative',
            borderRadius: 0
        },
        markerCardBody: {
            padding: '0.5em !important',
            '& h6': {
                fontSize: '1.3em'
            }
        },
        emptyMarkerHolder: {
            height: '35em',
            display: 'flex',
            alignItems: 'center',
            '& h1': {
                width: '100%',
                textAlign: 'center',
                color: 'gray'
            }
        },
        footNote: {
            float: 'right',
            marginTop: '0.5em',
            color: 'gray',
            fontWeight: 'bold'
        },
        tablePaper: {
            maxHeight: '70%',
            padding: '0 2em 2em 2em',
            overflowY: 'scroll'
        },
        markerLimitAlert: {
            width: '85%',
        },
        formControl: {
            margin: theme.spacing(3),
        },
        addButton: {
            width: '100%',
            margin: '0',
            marginBottom: '1em',
            marginTop: '0.5em'
        },
    });

interface Props extends WithStyles<typeof styles> {
    className?: string;
    rootStore: RootStore;
    currentAccount: AccountFull;
    currentNodeTree: RenderTree;
    folders: Folder[];
    accountProvider: AccountProvider;
    folderProvider: FolderProvider;
    floorplanProvider: FloorplanProvider;
    jobProvider: JobProvider;
    processJobs?: (
        jobs: RunningTasks[],
        sourceList: MarkerSourceItem[]
    ) => void;
}

interface States {
    sourceList: MarkerSourceItem[];
    currentStage: WizardSteps;
    currentNodeTree: RenderTree;
    loadedMarkers: Marker[];
    treeIsLoading: boolean;
    treePickedType: PickedType;
    treePickedId: string;
    treePickedName: string;
    treePickedSourceType: MarkerSourceType;
    folders: Folder[];
    processedFolders: string[];
    processedFoldersWMarkers: string[];
    inputFields: fieldInput[];
    isContentLoading: boolean;
    producedJobsList: RunningTasks[];
    processedMarkers: number;
    failedMarkers: number;
    treeScrollPosition: number;
    isNotificationModalOpened: boolean;
    notificationModalHeader: string;
    notificationModalText: string;
}   

class MarkerExportWizard extends React.Component<Props, States> {

    state: States = {
        sourceList: [],
        currentStage: WizardSteps.MarkerOrigin,
        currentNodeTree: { id: '', name: '', children: [], path: '' },
        loadedMarkers: [],
        treeIsLoading: false,
        treePickedType: PickedType.None,
        treePickedId: '',
        treePickedName: '',
        treePickedSourceType: 'None',
        folders: [],
        processedFolders: [],
        processedFoldersWMarkers: [],
        inputFields: VALID_EXPORT_FIELDS,
        isContentLoading: false,
        producedJobsList: [],
        processedMarkers: 0,
        failedMarkers: 0,
        treeScrollPosition: 0,
        isNotificationModalOpened: false,
        notificationModalHeader: '',
        notificationModalText: ''
    };

    componentDidMount() {
        this.setUpInitialTree();
    }

    setUpInitialTree = () => {
        // COPY THE ALREADY NAVIGATED TREE FOR BETTER UX
        const { currentNodeTree, folders } = this.props;
        this.setState({ currentNodeTree, folders });
    };

    deepTreeSearch = (treeData: RenderTree, searchId: string) => {
        let foundTree: RenderTree | undefined;
        let searchLevel: RenderTree[] | undefined = treeData.children;
        do {
            foundTree = searchLevel?.find((e: RenderTree) => e.id === searchId);
            let tempLevel: any = [];
            searchLevel?.forEach((element: RenderTree) => {
                tempLevel = tempLevel.concat(element.children);
            });
            searchLevel = tempLevel;
        } while (!foundTree);
        return foundTree;
    };

    updateChildrenOnPath = (
        treeData: RenderTree,
        nodeId: string,
        children: RenderTree[]
    ) => {
        treeData.children!.forEach(element => {
            if (_.isEqual(element.id, nodeId)) {
                element.children = children;
            } else {
                this.updateChildrenOnPath(element, nodeId, children);
            }
        });
    };

    checkCanSelectButton = () => {
        const {
            sourceList,
            treePickedId,
            treePickedSourceType,
            treePickedType
        } = this.state;

        if (sourceList.find(e => e.id === treePickedId)) {
            return true;
        }

        if (sourceList.find(e => e.type === 'Intermediate Folder')) {
            return true;
        }

        if (treePickedType === PickedType.None) {
            return true;
        }

        if (treePickedSourceType === 'None') {
            return true;
        }

        if (treePickedSourceType === 'Intermediate Folder' && sourceList.length > 0){
            return true;
        }

        return false;
    };

    addExportEntry = (
        pickedType: PickedType,
        sourceType: MarkerSourceType,
        pickedId: string,
        pickedName: string,
        currentNodeTree: RenderTree
    ) => {
        this.setState({
            treePickedType: pickedType,
            treePickedId: pickedId,
            treePickedName: pickedName,
            treePickedSourceType: sourceType,
            treeIsLoading: false,
            currentNodeTree
        });
    };

    addExportItem = () => {
        const {
            sourceList,
            treePickedId,
            treePickedName,
            treePickedSourceType
        } = this.state;

        if (sourceList.find(e => e.id === treePickedId)) {
            this.setState({
                treePickedType: PickedType.None,
                treePickedSourceType: 'None'
            });
            return;
        }

        if (treePickedSourceType !== 'None' && treePickedId && treePickedName && !sourceList.find(e => e.type === 'Intermediate Folder')) {
            if (treePickedSourceType === 'Intermediate Folder') {
                if(!sourceList.find(e => e.type === 'Intermediate Folder') && sourceList.length <= 0) {
                    sourceList.push({
                        id: treePickedId,
                        name: treePickedName,
                        type: treePickedSourceType
                    });
                }
            } else {
                sourceList.push({
                    id: treePickedId,
                    name: treePickedName,
                    type: treePickedSourceType
                });
            }
        };

        this.setState({
            treePickedType: PickedType.None,
            treePickedSourceType: 'None',
            sourceList
        });
    };

    onNodeSelected = (
        treeData: RenderTree,
        nodeId: string,
        nodeLabel: string,
        updateCallback: (nodeIds: string[]) => void
    ) => {
        const { currentAccount, accountProvider, folderProvider } = this.props;
        const {
            folders,
            processedFolders,
            processedFoldersWMarkers,
            currentNodeTree
        } = this.state;
        if (currentAccount!.account_id === nodeId) {
            this.addExportEntry(
                PickedType.None,
                'None',
                '',
                '',
                currentNodeTree
            );
            return;
        }
        this.setState({ treeIsLoading: true });
        // CHECK PICK TYPE
        const currentNodePos = this.deepTreeSearch(treeData, nodeId);
        if(folders.find(e => e.id === nodeId)) {
            // DID WE ALREADY CHECK FOR CHILDREN?
            if (processedFolders.find(e => e === nodeId)) {
                // FOLDER CHILDREN
                // CHECK IF THIS IS THE ROOT FOLDER
                if (currentNodePos.path === "") {
                    // YOU CANNOT CHOOSE THE ROOT FOLDER FOR EXPORT
                    this.addExportEntry(
                        PickedType.None,
                        'None',
                        '',
                        '',
                        treeData
                    );
                    return;
                }
                // THIS IS NOT THE ROOT FOLDER WE CAN CHOOSE THIS ONE
                this.addExportEntry(
                    PickedType.Folder,
                    processedFoldersWMarkers.find(e => e === nodeId) ? 'Folder' : 'Intermediate Folder',
                    nodeId,
                    currentNodePos.name,
                    treeData
                );
                return;
            }
            // EMPTY
            // CHECK FOR FOLDER CHILDREN FIRST
            const currentPath = currentNodePos.path.slice(0, -2);
            processedFolders.push(nodeId);
            return accountProvider
                .getChildrenFolderByFolderId(currentAccount!.account_id, nodeId)
                .then((results: Folder[]) => {
                    if (results.length > 0) {
                        const nodes: RenderTree[] = results.map(element => {
                            const newItem = {
                                id: element.id,
                                name: element.name,
                                children: [],
                                path: `${currentNodePos.path}${element.id}__`
                            };
                            return newItem as RenderTree;
                        });
                        this.updateChildrenOnPath(treeData, nodeId, nodes);
                        updateCallback([
                            currentAccount!.account_id,
                            ...currentPath.split('__'),
                            ...nodes.map(e => e.id)
                        ]);

                        // CHECK IF THIS IS THE ROOT FOLDER
                        this.setState({folders: folders.concat(results)});
                        if (currentNodePos.path === "") {
                            // YOU CANNOT CHOOSE THE ROOT FOLDER FOR EXPORT
                            this.addExportEntry(
                                PickedType.None,
                                'None',
                                '',
                                '',
                                treeData
                            );
                            return;
                        }
                        // THIS IS NOT THE ROOT FOLDER WE CAN CHOOSE THIS ONE
                        this.addExportEntry(
                            PickedType.Folder,
                            'Intermediate Folder',
                            nodeId,
                            currentNodePos.name,
                            treeData
                        );
                        return;
                    } else {
                        return folderProvider
                            .fetchFloorplans(nodeId)
                            .then(floorplans => {
                                this.setState({processedFolders});
                                if (floorplans.length > 0) {
                                    const nodes: RenderTree[] = floorplans.map(
                                        (element: RenderTree) => {
                                            const newItem = {
                                                ...element
                                            } as RenderTree;
                                            newItem.children = [];
                                            newItem.path = `${currentNodePos.path}${element.id}__`;
                                            return newItem;
                                        }
                                    );
                                    this.updateChildrenOnPath(
                                        treeData,
                                        nodeId,
                                        nodes
                                    );
                                    updateCallback([
                                        currentAccount!.account_id,
                                        ...currentPath.split('__'),
                                        ...nodes.map(e => e.id)
                                    ]);
                                    // THIS FOLDER FOLDER HAS FLOORPLAN
                                    // IT IS SELECTABLE
                                    processedFoldersWMarkers.push(nodeId);
                                    this.setState({processedFoldersWMarkers});
                                    this.addExportEntry(
                                        PickedType.Folder,
                                        'Folder',
                                        nodeId,
                                        currentNodePos.name,
                                        treeData
                                    );
                                    return;
                                }
                                // NO FLOORPLANS FOUND
                                // CHECK IF IT HAS MARKERS
                                return folderProvider
                                    .fetchFolderMarkers(nodeId)
                                    .then(folderMarkers => {
                                        const { markers } = folderMarkers;

                                        if (markers.length <= 0) {
                                            // NO MARKERS FOUND
                                            // CAN'T DO ANYTHING
                                            this.addExportEntry(
                                                PickedType.None,
                                                'None',
                                                '',
                                                '',
                                                treeData
                                            );
                                            return;
                                        }

                                        processedFoldersWMarkers.push(nodeId);
                                        this.setState({processedFoldersWMarkers});
                                        this.addExportEntry(
                                            PickedType.Folder,
                                            'Folder',
                                            nodeId,
                                            currentNodePos.name,
                                            treeData
                                        );

                                        return;
                                    });
                            });
                    }
                });
        }
        this.addExportEntry(
            PickedType.FloorPlan,
            'Floorplan',
            nodeId,
            currentNodePos.name,
            treeData
        );

        return;
    };

    onNodeToggled = (nodeIds: string[]) => {
        return;
    };

    deleteSourceList = (listItem: SimpleListItem) => {
        const { sourceList } = this.state;

        const delIndex = sourceList.findIndex(e => e.id === listItem.id);

        if (delIndex >= 0) {
            sourceList.splice(delIndex, 1);
            this.setState({ sourceList });
        }

        return;
    };

    checkNextStageButton = () => {
        const { currentStage } = this.state;
        let flag = false;

        switch (currentStage) {
            case WizardSteps.MarkerOrigin:
                const { sourceList } = this.state;
                flag = sourceList.length <= 0;
                break;

            case WizardSteps.Result:
                const { isContentLoading } = this.state;
                flag = isContentLoading;
                break;
            default:
                break;
        }

        return flag;
    };

    goNextStageButton = () => {
        const { currentStage, sourceList } = this.state;

        switch (currentStage) {
            case WizardSteps.MarkerOrigin:
                if (sourceList.length <= 0) {
                    break;
                }

                this.setState({ currentStage: WizardSteps.FieldSelection });
                this.loadSelectedMarkers();
                break;

            case WizardSteps.FieldSelection:
                this.setState({ currentStage: WizardSteps.Confirmation });
                break;

            case WizardSteps.Confirmation:
                this.sendExportJob();
                this.setState({ currentStage: WizardSteps.Result });
                break;

            case WizardSteps.Result:
                const { processJobs } = this.props;

                if (!processJobs) {
                    break;
                }

                const { producedJobsList } = this.state;
                processJobs(producedJobsList, sourceList);
                break;

            default:
                break;
        }
    };

    loadSelectedMarkers = () => {
        const { sourceList } = this.state;
        const { floorplanProvider, folderProvider } = this.props;
        let elapsed = 0;
        let allMarkers: Marker[] = [];

        this.setState({ isContentLoading: true });
        sourceList.forEach(source => {
            if (source.type === 'Floorplan') {
                floorplanProvider.fetchMarkers(source.id).then(data => {
                    allMarkers = allMarkers.concat(data);
                    elapsed += 1;

                    if (elapsed === sourceList.length) {
                        this.setState({
                            loadedMarkers: allMarkers,
                            isContentLoading: false
                        });
                    }
                });
                return;
            } else if(source.type === 'Folder') {
                folderProvider.fetchFolderMarkers(source.id).then(data => {
                    const { markers } = data;
                    allMarkers = allMarkers.concat(markers);
                    elapsed += 1;
    
                    if (elapsed === sourceList.length) {
                        this.setState({
                            loadedMarkers: allMarkers,
                            isContentLoading: false
                        });
                    }
                });
            } else {
                allMarkers = [{
                    _id: "intermediate_folder",
                    name: source.name,
                    marker_id: "Intermediate Folder"
                } as Marker];
                this.setState({
                    loadedMarkers: allMarkers,
                    isContentLoading: false
                });
            }
        });
    };

    sendExportJob = () => {
        const {
            sourceList,
            producedJobsList,
            inputFields,
            loadedMarkers
        } = this.state;
        const {
            rootStore: { adminStore },
            currentAccount,
            jobProvider
        } = this.props;

        if (sourceList.length <= 0) {
            return;
        }
        
        const userIdentifiers = adminStore.userIdentifiers!;
        const jobPayload: JobRequest = {
            user_created_id: userIdentifiers.user_id,
            account_id: currentAccount.account_id,
            job_type: `${JobTypes.ComplexMarkerExport}`,
            additional_params: {
                export_origins: sourceList,
                export_fields: inputFields.filter(e => e.value === true)
            }
        };

        this.setState({
            isContentLoading: true,
            currentStage: WizardSteps.Result
        });
        jobProvider.scheduleJob(jobPayload)
            .then((result) => {
                const newTask: RunningTasks = {
                    account: currentAccount,
                    export_name: `Complex marker export for ${currentAccount.name}`,
                    export_id: result.job_id,
                    type: JobTypes.ComplexMarkerExport,
                    task_id: result.job_id,
                    status: 102,
                    startedAt: Date.now(),
                    elapsedTime: 0,
                    progress: 0,
                    timer: 1500,
                    static_progress_count: 0,
                    set_to_stop: false
                };

                producedJobsList.push(newTask);
                this.setState({
                    isContentLoading: false,
                    processedMarkers: loadedMarkers.length
                });
            }).catch((error) => {
                // tslint:disable-next-line:no-console
                console.error(error);
                this.setState({
                    failedMarkers: loadedMarkers.length
                });
            });
    };

    centerScroll = (evt: React.UIEvent<HTMLElement>) => {
        this.setState({
            treeScrollPosition: evt.currentTarget.scrollTop
        });
    };

    onNotificationModalClicked = () => {
        this.setState({isNotificationModalOpened: false});   
    };

    onExportFieldChange = (event: React.ChangeEvent<HTMLInputElement>, checked: boolean, index: number) => {
        const { inputFields } = this.state;

        inputFields[index].value = checked;

        this.setState({inputFields});
    };

    resetStageButton = () => {
        this.setState({
            treeIsLoading: false,
            treePickedId: '',
            treePickedName: '',
            treePickedType: PickedType.None,
            treePickedSourceType: 'None',
            sourceList: [],
            failedMarkers: 0,
            processedMarkers: 0,
            loadedMarkers: [],
            currentStage: WizardSteps.MarkerOrigin
        })
    };

    public render() {
        const { className, classes } = this.props;
        const {
            currentStage,
            currentNodeTree,
            treeIsLoading,
            sourceList,
            isContentLoading,
            failedMarkers,
            processedMarkers,
            treeScrollPosition,
            isNotificationModalOpened,
            notificationModalHeader,
            notificationModalText,
            loadedMarkers,
            inputFields
        } = this.state;

        const sourceListItems = sourceList
            ? sourceList.map(item => {
                  return new SimpleListItem(
                      item.id,
                      `${item.type}: ${item.name}`,
                      item.id
                  );
              })
            : [];

        return (
            <div
                className={`${classes.root} ${className}`}
                data-testid="marker-complex-export-wizard"
            >
                <div className={`${classes.container}`}>
                    <Stepper
                        activeStep={currentStage}
                        className={classes.stepsHolder}
                        alternativeLabel
                    >
                        {WIZARD_STEPS.map(step => (
                            <Step key={step.id}>
                                <StepLabel>{step.label}</StepLabel>
                            </Step>
                        ))}
                    </Stepper>
                    <div className={classes.stepsContentHolder}>
                        {currentStage === WizardSteps.MarkerOrigin ? (
                            <React.Fragment>
                                <Grid container spacing={1} alignItems="flex-start" className={classes.gridHolders} data-testid="origin-step">
                                <Grid item xs={8} className={classes.gridHolders}>
                                    <Typography
                                        variant='h6'
                                        className={`${classes.heading} ${classes.ellipsis}`}
                                    >
                                        Select floorplans or folders
                                    </Typography>
                                    <Paper
                                        className={classes.treePaper}
                                        onScrollCapture={this.centerScroll}
                                    >
                                        <NodesTreeView
                                            treeData={currentNodeTree}
                                            isLoading={treeIsLoading}
                                            onNodeSelected={this.onNodeSelected}
                                            onNodeToggled={this.onNodeToggled}
                                            className={classes.treeContainer}
                                            loadingOverlayPosition={treeScrollPosition}
                                        />
                                    </Paper>
                                </Grid>
                                <Grid item xs={4} data-testid="selected-origin-documents">
                                    <Typography
                                        variant='h6'
                                        className={`${classes.heading} ${classes.ellipsis}`}
                                    >
                                        Floorplans / Folders selected
                                    </Typography>
                                    <SimpleListPagination
                                        className={classes.simpleList}
                                        keepItemSelected={false}
                                        items={sourceListItems}
                                        canDeleteListItem={true}
                                        onDeleteItemClick={this.deleteSourceList}
                                        rowsPerPage={10}
                                        totalItems={sourceListItems.length}
                                        offset={0}
                                        noItemsLabel="Nothing selected"
                                    />
                                    <Divider />
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        className={classes.addButton}
                                        onClick={this.addExportItem}
                                        disabled={this.checkCanSelectButton()}
                                    >
                                        Add Item to export list
                                    </Button>
                                </Grid>
                            </Grid>
                        </React.Fragment>
                        ) : currentStage === WizardSteps.FieldSelection ?
                            <React.Fragment>
                                <Grid container spacing={1} alignItems="flex-start" className={classes.gridHolders} data-testid="field-selection-step">
                                    <Grid item xs={1} />
                                    <Grid item xs={10}>
                                        <Paper className={classes.tablePaper}>
                                            <Typography
                                                variant='h6'
                                                className={`${classes.simpleHeading} ${classes.ellipsis}`}
                                            >
                                                Select fields to be exported
                                            </Typography>
                                            <br />
                                            <FormControl component="fieldset" className={classes.formControl}>
                                                <FormGroup>
                                                    {
                                                        inputFields.map((field, index) => (
                                                            index <= 7 &&
                                                                <FormControlLabel
                                                                    control={
                                                                        <Checkbox checked={field.value}
                                                                        name={field.field}
                                                                        onChange={(evt, checked) => this.onExportFieldChange(evt, checked, index)}/>
                                                                    }
                                                                    label={field.label}
                                                                />
                                                        ))
                                                    }
                                                </FormGroup>
                                            </FormControl>
                                            <FormControl component="fieldset" className={classes.formControl}>
                                                <FormGroup>
                                                    {
                                                        inputFields.map((field, index) => (
                                                            index > 7 &&
                                                                <FormControlLabel
                                                                    control={
                                                                        <Checkbox checked={field.value}
                                                                        name={field.field}
                                                                        onChange={(evt, checked) => this.onExportFieldChange(evt, checked, index)}/>
                                                                    }
                                                                    label={field.label}
                                                                />
                                                        ))
                                                    }
                                                </FormGroup>
                                            </FormControl>
                                        </Paper>
                                    </Grid>
                                    <Grid item xs={1} />
                                </Grid> 
                            </React.Fragment>
                        : currentStage === WizardSteps.Confirmation ? (
                            <React.Fragment>
                                <Grid container spacing={1} alignItems="flex-start" className={classes.gridHolders} data-testid="confirmation-step">
                                    <Grid item xs={1} />
                                    <Grid item xs={5} className={classes.gridHolders}>
                                        <Typography
                                            variant='h6'
                                            className={`${classes.simpleHeading} ${classes.ellipsis}`}
                                        >
                                            Exporting markers
                                        </Typography>
                                        <Paper className={classes.treePaper}>
                                            {
                                                loadedMarkers.map(m => {
                                                    return (
                                                    <Card className={classes.markerCard}>
                                                        <CardContent className={classes.markerCardBody}>
                                                            <Typography variant="h6" component="h6">
                                                                {m.name}
                                                            </Typography>
                                                            <Typography color="textSecondary">
                                                                {m.marker_id}
                                                            </Typography>
                                                        </CardContent>
                                                    </Card>
                                                    );
                                                })
                                            }
                                        </Paper>
                                        <div className={classes.footNote}>
                                            {loadedMarkers.length} Markers
                                        </div>
                                    </Grid>
                                    <Grid item xs={5} className={classes.gridHolders}>
                                        <Typography
                                            variant='h6'
                                            className={`${classes.simpleHeading} ${classes.ellipsis}`}
                                        >
                                            Selected fields
                                        </Typography>
                                        <Paper className={classes.treePaper}>
                                            {
                                                inputFields.map(e => (
                                                    e.value && 
                                                        <Card className={classes.markerCard}>
                                                            <CardContent className={classes.markerCardBody}>
                                                                <Typography variant="h6" component="h6">
                                                                    {e.label}
                                                                </Typography>
                                                            </CardContent>
                                                        </Card>
                                                ))
                                            }
                                        </Paper>
                                    </Grid>
                                    <Grid item xs={1} />
                                </Grid>
                            </React.Fragment>
                        ) : (
                            <div data-testid="result-step">
                                <CCSpinner
                                    label="Processing Markers"
                                    className={classes.progressContainer}
                                    loading={isContentLoading}
                                    size={200}
                                >
                                    <div className={classes.emptyMarkerHolder}>
                                        <Typography variant="h6" component="h1">
                                            {
                                                processedMarkers > 0 
                                                ?
                                                <React.Fragment>
                                                    All markers have been processed for export: 
                                                    <br />
                                                    <b>
                                                        Total exported markers {failedMarkers + processedMarkers}
                                                    </b>
                                                </React.Fragment>
                                                :
                                                <React.Fragment>
                                                    No operation has been performed: 
                                                    <br />
                                                    <b>
                                                        No marker has been exported
                                                    </b>
                                                </React.Fragment>
                                            }
                                            <br />
                                            <Button
                                                variant="contained"
                                                color="primary"
                                                className={classes.centerButton}
                                                data-testid="run-another-button"
                                                onClick={this.resetStageButton}
                                            >
                                                Run Another Export
                                            </Button>
                                        </Typography>
                                    </div>
                                </CCSpinner>
                            </div>
                        )}
                    </div>
                    {
                        currentStage === WizardSteps.MarkerOrigin &&
                            <Alert severity="info" className={classes.markerLimitAlert}>
                                <AlertTitle>INTERMEDIATE FOLDER SELECTION</AlertTitle>
                                You can choose an intermediate folder for the export. Please keep in mind that if this folder contains many subfolders, 
                                the process might take a long time. Also, only one export process per intermediate folder can run at a time
                                across the entire system.
                            </Alert>
                    }
                    <Button
                        variant="contained"
                        color="primary"
                        className={classes.nextButton}
                        disabled={this.checkNextStageButton()}
                        onClick={this.goNextStageButton}
                        data-testid="go-next-step-button"
                    >
                        {currentStage !== WizardSteps.Result
                            ? 'Next'
                            : 'Finish'}
                    </Button>
                </div>
                <SimpleDialog
                    open={isNotificationModalOpened}
                    titleText={notificationModalHeader}
                    contentText={notificationModalText}
                    buttonCancelLabel=""
                    onDialogResult={this.onNotificationModalClicked}
                />
            </div>
        );
    }
}

const MUIComponent = withStyles(styles)(MarkerExportWizard);
export { MUIComponent as MarkerExportWizard };
